"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const vue_1 = require("vue");
const vue_i18n_1 = require("vue-i18n");
const use_file_validation_1 = require("./use-file-validation");
function useValidationText(elementRef, displayName, options, externalErrorMessage, requiredErrorMessage) {
    const { t } = (0, vue_i18n_1.useI18n)();
    const { isFileSizeError, isFileTypeError, getFormattedAcceptString } = (0, use_file_validation_1.useFileValidation)(elementRef, options);
    const numberOfSubmits = (0, vue_1.inject)("numberOfSubmits", (0, vue_1.ref)(0));
    const formValidationResetControl = (0, vue_1.inject)("formValidationResetControl", (0, vue_1.ref)(0));
    const hasFormWrapper = (0, vue_1.inject)("hasFormWrapper", false);
    const updateFormValidity = (0, vue_1.inject)("updateFormValidity", () => { });
    const requiredError = (0, vue_1.ref)(false);
    const patternError = (0, vue_1.ref)(false);
    const typeError = (0, vue_1.ref)(false);
    const fileTypeError = (0, vue_1.ref)(false);
    const fileSizeError = (0, vue_1.ref)(false);
    const rangeOverflowError = (0, vue_1.ref)(false);
    const rangeUnderflowError = (0, vue_1.ref)(false);
    const tooLongError = (0, vue_1.ref)(false);
    const tooShortError = (0, vue_1.ref)(false);
    const badInputError = (0, vue_1.ref)(false);
    const isLiveValidation = (0, vue_1.ref)(false);
    const isValidationVisible = (0, vue_1.computed)(() => {
        const hasExternalErrorMessage = !!externalErrorMessage?.value;
        const hasInternalErrorMessage = !!errorMessage.value;
        const wasSubmitted = numberOfSubmits.value > 0;
        const internalValidationActive = isLiveValidation.value || wasSubmitted;
        return hasExternalErrorMessage || (hasInternalErrorMessage && internalValidationActive);
    });
    // Helfer für initialLiveValidation, damit bei Validation Reset (formValidationResetControl) die Validierung nicht kurz "aufblitzt"
    let initialLiveValidationTimeout = 0;
    const updateValidateStates = () => {
        if (!elementRef.value)
            return;
        // patternMissmatch hat höhere Priorität wie typeMismatch (z.B. wichtig bei E-Mail-Pattern)
        const hasPattern = elementRef.value?.getAttribute("pattern");
        requiredError.value = elementRef.value.validity.valueMissing;
        rangeOverflowError.value = elementRef.value.validity.rangeOverflow;
        rangeUnderflowError.value = elementRef.value.validity.rangeUnderflow;
        tooLongError.value = elementRef.value.validity.tooLong;
        tooShortError.value = elementRef.value.validity.tooShort;
        badInputError.value = elementRef.value.validity.badInput;
        patternError.value = elementRef.value.validity.patternMismatch;
        typeError.value = hasPattern ? false : elementRef.value.validity.typeMismatch;
        fileTypeError.value = isFileTypeError();
        fileSizeError.value = isFileSizeError();
    };
    // eslint-disable-next-line complexity
    const errorMessage = (0, vue_1.computed)(() => {
        if (externalErrorMessage?.value)
            return externalErrorMessage?.value;
        if (requiredError.value)
            return requiredErrorMessage || t("shared.validation.requiredError", [displayName]);
        if (patternError.value)
            return options?.patternErrorMessage || t("shared.validation.invalidError", [displayName]);
        if (typeError.value)
            return t("shared.validation.invalidError", [displayName]);
        if (fileTypeError.value)
            return t("shared.validation.fileTypeError", [getFormattedAcceptString()]);
        if (fileSizeError.value)
            return t("shared.validation.fileSizeError", [options?.maxFileSizeMb]);
        if (rangeOverflowError.value)
            return elementRef.value?.type === "date"
                ? t("shared.validation.rangeOverflowDateError", [displayName])
                : t("shared.validation.rangeOverflowError", [displayName]);
        if (rangeUnderflowError.value)
            return elementRef.value?.type === "date"
                ? t("shared.validation.rangeUnderflowDateError", [displayName])
                : t("shared.validation.rangeUnderflowError", [displayName]);
        if (tooLongError.value)
            return t("shared.validation.tooLongError", [displayName]);
        if (tooShortError.value)
            return t("shared.validation.tooShortError", [displayName]);
        if (badInputError.value)
            return t("shared.validation.invalidError", [displayName]);
        return undefined;
    });
    // Update errorMessage on the input
    (0, vue_1.watch)(() => errorMessage.value, () => {
        if (!elementRef.value)
            return;
        elementRef.value.errorMessage = errorMessage.value;
        const newValidity = errorMessage.value || "";
        elementRef.value.setCustomValidity(newValidity);
    });
    (0, vue_1.watch)(() => numberOfSubmits.value, (isSubmitTriggered) => {
        if (isSubmitTriggered)
            updateValidateStates();
    });
    (0, vue_1.watch)(() => formValidationResetControl.value, () => {
        window.clearTimeout(initialLiveValidationTimeout);
        initialLiveValidationTimeout = 0;
        isLiveValidation.value = false;
    });
    (0, vue_1.watch)(() => isValidationVisible.value, () => {
        (0, vue_1.nextTick)(updateFormValidity); // next tick to before update the computed error message
    });
    const handleInput = () => (0, vue_1.nextTick)(updateValidateStates);
    const handleChange = (startLiveValidation) => {
        updateValidateStates();
        if ((startLiveValidation || !hasFormWrapper) && errorMessage.value) {
            doLiveValidation();
        }
    };
    const handleBlur = handleChange;
    const doLiveValidation = () => {
        // delay live validation to prevent flickering on login layer/landing pages (switching between login and register)
        if (!initialLiveValidationTimeout && !isLiveValidation.value) {
            initialLiveValidationTimeout = window.setTimeout(() => {
                isLiveValidation.value = true;
            }, 180);
        }
    };
    return {
        errorMessage,
        handleChange,
        handleBlur,
        handleInput,
        isValidationVisible,
    };
}
exports.default = useValidationText;
